<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePrefs } from '@/stores/prefs'
import { useSemesters } from '@/stores/semesters'
import { useCourseList } from '@/composables/useCourseList'
import SemesterSwitch from '@/components/SemesterSwitch.vue'
import CourseTable from '@/components/CourseTable.vue'
import '@/styles/page-fill.css'

const { t, locale } = useI18n()
const prefs = usePrefs()
const semesters = useSemesters()
const { search, query, page, itemsPerPage, sortBy, rows, total, loading, error, canFilterMine, onlyMine, reload, clearSearch } =
    useCourseList()

const semesterName = computed(() => {
    const tab = semesters.tabs.find((candidate) => candidate.code === semesters.selected)
    return tab ? (locale.value === 'en' ? tab.nameEn : tab.nameCs) || tab.code : ''
})

const empty = computed(() => {
    if (query.value !== '') {
        return { message: t('courses.noMatch'), hint: t('courses.noMatchHint'), action: t('courses.clearSearch') }
    }
    return { message: onlyMine.value ? t('courses.emptyMine') : t('courses.empty') }
})
</script>

<template>
    <v-container class="courses-page page-fill" max-width="1280">
        <header class="courses-page__header">
            <h1 class="courses-page__title text-headline-small">
                <span>{{ t('courses.title') }}</span> <span v-if="semesterName" class="courses-page__semester">· {{ semesterName }}</span>
            </h1>
            <div class="courses-page__actions">
                <SemesterSwitch :tabs="semesters.tabs" :model-value="semesters.selected" @update:model-value="semesters.select" />
            </div>
        </header>

        <v-card class="courses-page__card page-fill__grow" border flat>
            <div class="courses-page__toolbar">
                <v-text-field
                    v-model="search"
                    data-test="course-filter"
                    class="courses-page__search"
                    :placeholder="t('courses.search')"
                    :aria-label="t('courses.search')"
                    prepend-inner-icon="mdi-magnify"
                    variant="solo-filled"
                    bg-color="surface-container"
                    density="compact"
                    flat
                    clearable
                    single-line
                    hide-details
                />
                <v-chip
                    v-if="canFilterMine"
                    data-test="only-mine"
                    class="courses-page__mine"
                    :aria-pressed="onlyMine ? 'true' : 'false'"
                    :variant="onlyMine ? 'flat' : 'outlined'"
                    :color="onlyMine ? 'secondary-container' : undefined"
                    :prepend-icon="onlyMine ? 'mdi-check' : 'mdi-account-outline'"
                    @click="prefs.setOnlyMine(!onlyMine)"
                >
                    {{ t('courses.onlyMine') }}
                </v-chip>
            </div>
            <v-divider />
            <v-alert v-if="error" class="ma-4" type="error" variant="tonal" icon="mdi-alert-circle-outline">
                {{ t('courses.error', { detail: error.detail || error.title }) }}
                <template #append>
                    <v-btn data-test="retry" variant="text" prepend-icon="mdi-refresh" @click="reload">
                        {{ t('courses.retry') }}
                    </v-btn>
                </template>
            </v-alert>
            <CourseTable
                v-else
                v-model:page="page"
                v-model:items-per-page="itemsPerPage"
                v-model:sort-by="sortBy"
                :rows="rows"
                :items-length="total"
                :loading="loading"
                :semester="semesters.selected ?? ''"
                :empty-message="empty.message"
                :empty-hint="empty.hint"
                :empty-action="empty.action"
                @click:empty-action="clearSearch"
            />
        </v-card>
    </v-container>
</template>

<style scoped>
/* As tall as the compact search field beside it (40 px, like the other controls). */
.courses-page__mine {
    height: 40px;
}

.courses-page {
    /* The bottom padding is the table's gap to the window edge (page-fill.css). */
    padding-block: 16px;
}

.courses-page__header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px 24px;
    margin-block-end: 16px;
}

.courses-page__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
}

.courses-page__title {
    font-weight: 500;
}

.courses-page__semester {
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    font-weight: 400;
}

.courses-page__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
    padding: 16px;
}

.courses-page__search {
    flex: 1 1 280px;
    max-width: 480px;
}

/* Material 3 search bar: a flat filled pill whose focus is a ring, not a darker fill. */
.courses-page__search :deep(.v-field__overlay) {
    opacity: 0 !important;
}

.courses-page__search :deep(.v-field) {
    transition: box-shadow 0.15s ease;
}

.courses-page__search :deep(.v-field--focused) {
    box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.55);
}

.courses-page__mine {
    font-weight: 500;
}

.courses-page__mine.v-chip--variant-outlined {
    border-color: rgba(var(--v-theme-on-surface), 0.24);
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

@media (max-width: 599px) {
    .courses-page {
        padding-block: 20px 32px;
    }

    .courses-page__search {
        max-width: none;
    }
}
</style>
